/**
 * The Operations page's rules (Design's sketch, Nino approved 18 Oct,
 * laptop only; built 19 Oct on Nino's go): one control tower for a drill
 * day. Everything here reads only what the server sends; a part it doesn't
 * send yet is "unknown", never guessed.
 *
 *   - Rooms: each live drill with its phase, time left, facilitator,
 *     learners and recording, and its problems in words with what to do.
 *     Problems first; a quiet room is a slim row.
 *   - The timeline (today or tomorrow) and the next 2 hours come from the
 *     coaching calendar; tomorrow's check flags what to fix tonight.
 *   - Times are Jakarta time (WIB), 24-hour.
 */
import type { ActiveDrillSummary, CalendarSession, SchedulerSlot } from '@/lib/api';

export const PHASES = ['Lobby', 'Act 1', 'Twist', 'Act 2', 'Debrief'] as const;
const PHASE_INDEX: Record<string, number> = { setup: 0, act1: 1, twist: 2, act2: 3, debrief: 4 };

/** Late starts: amber from 1 min, red from 5 min (Live Drills agreed, 17 Oct). */
export const LATE_AMBER_MIN = 1;
export const LATE_RED_MIN = 5;

/**
 * A learner in the room unheard this long is a problem (Product's gap 1, Nino's go 26 Oct): the same
 * 3 minutes as Studio's "not heard" alert. The server's silent_since starts at 20 s; shorter quiet is
 * normal (listening), so the page waits for 3 min before it says anything.
 */
export const QUIET_MIN = 3;

const TZ = 'Asia/Jakarta';
const MIN = 60_000;

/** Minutes since midnight in Jakarta: "14:19" → 859. */
export function wibMinutes(iso: string | number | Date): number {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d);
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

/** "14:19" in Jakarta. */
export function wibClock(iso: string | number | Date): string {
  const m = wibMinutes(iso);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** "2026-10-19": the Jakarta date of a moment. */
export function wibDate(iso: string | number | Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

/** "15:19" in Singapore, where Nino works (Product's fix 4, Nino's go 19 Oct). */
export function sgtClock(iso: string | number | Date): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
}

/** "Monday 19 Oct". */
export function wibDayName(iso: string | number | Date): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'short' }).format(new Date(iso));
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const first = (name: string | null | undefined) => (name ?? '').trim().split(/\s+/)[0] || 'A learner';
const initials = (name: string | null | undefined) => (name ?? '').trim().split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
const listNames = (names: string[]) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

export type Level = 'red' | 'amber' | 'none';
export type Act = 'whatsapp-facilitator' | 'whatsapp-learners' | 'studio';

export interface RoomProblem {
  key: string;
  level: 'red' | 'amber';
  /** The problem, bold first part: "Not started · 9 min late." */
  head: string;
  /** More words: "Dewa isn't in the room." */
  tail?: string;
  /** What to do, as the button says it. */
  action: string;
  act: Act;
  /** When it began, for the events list. */
  since: string | null;
  /** For a WhatsApp to learners: each learner's first name and account id (null for a walk-in with no account). */
  who?: Array<{ id: string | null; name: string }>;
}

export interface HandledItem { key: string; head: string; by: string; at: string; note: string | null }

export type DotState = 'in' | 'quiet' | 'waiting' | 'dropped' | 'never' | 'out' | 'unknown';
export interface Dot { state: DotState; label: string; title: string }

export interface RoomView {
  id: string;
  title: string;
  /** "Online · 13:40–14:25" / "In person · UNU campus, room 3.2 · 14:00". */
  where: string;
  facilitator: { id: string; name: string } | null;
  /** true / false, or null when the server doesn't say. */
  here: boolean | null;
  /** true / false, or null when the server doesn't say yet. */
  recording: boolean | null;
  phase: number;
  /** The right-hand time: "9 min late" (red) over "Due 14:10"; "26 min left" over "Twist". */
  timeBig: string;
  timeSmall: string;
  late: boolean;
  dots: Dot[];
  /** "3 of 5 in · 2 never joined". */
  people: string;
  problems: RoomProblem[];
  /** Problems someone marked handled, with who, when and their note (Product's fix 5); null when the server doesn't say yet. */
  handled: HandledItem[] | null;
  level: Level;
  /** Jakarta minutes, for the timeline and matching. */
  startMin: number | null;
  endMin: number | null;
}

/** One live drill, read for the Operations page. */
export function roomView(d: ActiveDrillSummary, now = Date.now()): RoomView {
  const setup = d.phase === 'setup';
  const phase = PHASE_INDEX[d.phase] ?? 0;
  const fac = d.facilitator ?? null;
  const facName = fac?.name ? first(fac.name) : 'The facilitator';
  const here = d.facilitator_waiting ? false : typeof d.facilitator_present === 'boolean' ? d.facilitator_present : null;
  const start = d.scheduled_start ? Date.parse(d.scheduled_start) : NaN;
  const lateMin = setup && !Number.isNaN(start) ? Math.floor((now - start) / MIN) : 0;
  const late = lateMin >= LATE_AMBER_MIN;
  const remainingMin = Math.ceil(d.remaining_seconds / 60);
  const endAt = setup ? NaN : now + d.remaining_seconds * 1000;
  const startMin = !Number.isNaN(start) ? wibMinutes(start) : !setup ? wibMinutes(now - d.elapsed_seconds * 1000) : null;
  const endMin = !Number.isNaN(endAt) ? wibMinutes(endAt) : null;

  const mode = d.venue?.mode === 'onsite' || d.mode === 'onsite' ? 'In person' : d.mode === 'hybrid' ? 'Hybrid' : 'Online';
  const span = startMin !== null ? `${clockOf(startMin)}${endMin !== null ? `–${clockOf(endMin)}` : ''}` : '';
  const where = [mode === 'In person' && d.venue?.name ? `${mode} · ${d.venue.name}` : mode, span].filter(Boolean).join(' · ');

  let timeBig: string; let timeSmall: string;
  if (setup && late) { timeBig = `${lateMin} min late`; timeSmall = `Due ${wibClock(start)}`; }
  else if (setup) { timeBig = !Number.isNaN(start) ? `Starts ${wibClock(start)}` : 'In the lobby'; timeSmall = 'Lobby'; }
  else if (d.remaining_seconds < 0) { timeBig = `${Math.floor(-d.remaining_seconds / 60)} min over`; timeSmall = PHASES[phase]; }
  else { timeBig = `${remainingMin} min left`; timeSmall = PHASES[phase]; }

  // The learners: one dot each when the server lists them (Live Drills), else counts.
  let dots: Dot[]; let people: string;
  if (d.learners?.length) {
    const map: Record<string, DotState> = { in: 'in', lobby: 'waiting', dropped: 'dropped', never_joined: 'never' };
    const word: Record<DotState, string> = { in: 'in the room', quiet: 'in the room', waiting: 'waiting in the lobby', dropped: 'dropped', never: 'never joined', out: 'not connected', unknown: 'can’t check' };
    dots = d.learners.map((l) => {
      const q = l.state === 'in' ? quietMin(l.silent_since, now) : null;
      const s: DotState = q !== null ? 'quiet' : map[l.state] ?? 'unknown';
      return { state: s, label: initials(l.name), title: `${l.name || 'A learner'}: ${word[s]}${q !== null ? `, not heard for ${q} min` : ''}` };
    });
    const qt = dots.filter((x) => x.state === 'quiet').length;
    const inn = dots.filter((x) => x.state === 'in').length + qt; const wt = dots.filter((x) => x.state === 'waiting').length;
    const nj = dots.filter((x) => x.state === 'never').length; const dr = dots.filter((x) => x.state === 'dropped').length;
    people = setup ? `${wt} of ${dots.length} waiting in the lobby` : [`${inn} of ${dots.length} in`, qt ? `${qt} not heard` : '', dr ? `${dr} dropped` : '', nj ? `${nj} never joined` : ''].filter(Boolean).join(' · ');
  } else {
    const n = d.participant_count; const c = Math.min(d.connected_count, n);
    dots = Array.from({ length: n }, (_, i) => (i < c
      ? { state: setup ? 'waiting' : 'in', label: '', title: setup ? 'Waiting in the lobby' : 'In the room' }
      : { state: 'out', label: '', title: 'Not connected' }));
    people = setup ? `${c} of ${n} waiting in the lobby` : `${c} of ${n} in`;
  }

  const recording = d.recording ? d.recording.on : d.learners?.some((l) => typeof l.recording === 'boolean') ? d.learners.some((l) => l.recording) : null;

  // Problems in words, with what to do (Design's table).
  const problems: RoomProblem[] = [];
  const wa = fac ? `WhatsApp ${facName}` : 'No facilitator';
  if (setup && late) {
    problems.push({ key: 'late', level: lateMin >= LATE_RED_MIN ? 'red' : 'amber', head: `Not started · ${lateMin} min late.`, tail: here === false ? `${facName} isn’t in the room.` : undefined, action: wa, act: 'whatsapp-facilitator', since: d.scheduled_start ?? null });
  }
  if (d.facilitator_waiting) {
    const until = d.facilitator_wait_until ? Math.max(0, Math.ceil((Date.parse(d.facilitator_wait_until) - now) / MIN)) : 5;
    problems.push({ key: 'away', level: 'red', head: `${facName} is away.`, tail: `The drill ends in ${plural(until, 'min', 'min')} unless they return.`, action: wa, act: 'whatsapp-facilitator', since: d.facilitator_away_since ?? null });
  } else if (!setup && d.facilitator_present === false) {
    problems.push({ key: 'away', level: 'red', head: `${facName} isn’t in the room.`, action: wa, act: 'whatsapp-facilitator', since: d.facilitator_away_since ?? null });
  }
  if (!setup && d.learners?.length) {
    const never = d.learners.filter((l) => l.state === 'never_joined');
    if (never.length) problems.push({ key: 'never', level: 'amber', head: `${plural(never.length, 'learner')} never joined:`, tail: `${listNames(never.map((l) => first(l.name)))}.`, action: never.length === 1 ? `WhatsApp ${first(never[0].name)}` : 'WhatsApp them', act: 'whatsapp-learners', since: null, who: never.map((l) => ({ id: l.learner_id ?? null, name: first(l.name) })) });
    for (const l of d.learners.filter((x) => x.state === 'dropped')) {
      const ago = l.since ? Math.max(0, Math.floor((now - Date.parse(l.since)) / MIN)) : null;
      problems.push({ key: `dropped:${l.participant_id ?? l.name ?? ''}`.slice(0, 80), level: 'amber', head: `${first(l.name)} dropped${ago !== null ? ` ${ago} min ago` : ''}, not back.`, action: `WhatsApp ${first(l.name)}`, act: 'whatsapp-learners', since: l.since ?? null, who: [{ id: l.learner_id ?? null, name: first(l.name) }] });
    }
  } else if (!setup) {
    const missing = Math.max(0, d.participant_count - d.connected_count);
    if (missing) problems.push({ key: 'missing', level: 'amber', head: `${plural(missing, 'learner')} not connected.`, action: 'Watch in Studio', act: 'studio', since: null });
  }
  if (!setup && recording === false) problems.push({ key: 'recording', level: 'red', head: 'Recording is off.', action: fac ? `WhatsApp ${facName} to turn it on` : 'Watch in Studio', act: fac ? 'whatsapp-facilitator' : 'studio', since: null });
  if (!setup && d.remaining_seconds < 0) problems.push({ key: 'over', level: 'amber', head: `Running ${Math.floor(-d.remaining_seconds / 60)} min over.`, action: wa, act: 'whatsapp-facilitator', since: null });
  // Not heard for 3 min: the facilitator in the room checks the phone (Product's gap 1). The server's own
  // "not heard" alert and the page's count are one problem, under the server's key, so Mark handled holds.
  const notHeard = new Set((d.alert_list ?? []).filter((a) => a.code === 'not_heard').map((a) => first(a.name)));
  if (!setup) for (const l of d.learners ?? []) {
    const q = l.state === 'in' ? quietMin(l.silent_since, now) : null;
    if (q === null || notHeard.has(first(l.name))) continue;
    problems.push({ key: `alert:not_heard:${first(l.name)}`.slice(0, 80), level: 'amber', head: `${first(l.name)} hasn’t been heard for ${q} min.`, action: fac ? wa : 'Watch in Studio', act: fac ? 'whatsapp-facilitator' : 'studio', since: l.silent_since ?? null });
  }
  for (const a of d.alert_list ?? []) {
    const quiet = a.code === 'not_heard' && !!fac;
    problems.push({ key: a.key ?? `alert:${a.code}:${a.name || 'room'}`, level: 'amber', head: `${a.words}.`.replace(/\.\.$/, '.'), action: quiet ? wa : 'Watch in Studio', act: quiet ? 'whatsapp-facilitator' : 'studio', since: a.since ?? null });
  }

  // Handled on the server (Live Drills, asked 18 Oct): no longer open.
  const handled = new Set((d.handled ?? []).map((h) => h.key));
  const open = problems.filter((p) => !handled.has(p.key));
  const trace: HandledItem[] | null = d.handled
    ? d.handled.map((h) => ({ key: h.key, head: problems.find((p) => p.key === h.key)?.head ?? handledWords(h.key), by: staffName(h.by), at: h.at, note: h.note?.trim() || null }))
      .sort((a, b) => b.at.localeCompare(a.at))
    : null;
  const level: Level = open.some((p) => p.level === 'red') ? 'red' : open.length ? 'amber' : 'none';
  open.sort((a, b) => (a.level === b.level ? 0 : a.level === 'red' ? -1 : 1));

  return {
    id: d.session_id,
    title: d.workshop_title || 'Untitled drill',
    where,
    facilitator: fac,
    here,
    recording,
    phase,
    timeBig,
    timeSmall,
    late,
    dots,
    people,
    problems: open,
    handled: trace,
    level,
    startMin,
    endMin,
  };
}

/** Whole minutes a learner has been unheard, once it reaches QUIET_MIN; else null. */
function quietMin(silentSince: string | null | undefined, now: number): number | null {
  if (!silentSince) return null;
  const m = Math.floor((now - Date.parse(silentSince)) / MIN);
  return Number.isFinite(m) && m >= QUIET_MIN ? m : null;
}

/** A handled problem that has since cleared, in words. */
function handledWords(key: string): string {
  const [kind, name] = key.split(':');
  const words: Record<string, string> = { late: 'Late start.', away: 'Facilitator away.', never: 'Learners never joined.', missing: 'Learners not connected.', recording: 'Recording was off.', over: 'Ran over.', alert: 'An alert.' };
  if (kind === 'dropped' && name) return `${first(name)} dropped.`;
  if (kind === 'alert' && name === 'not_heard') return `${first(key.split(':')[2])} wasn’t heard.`;
  return words[kind] ?? 'A problem.';
}

/** "fuad@playbook.plus" → "Fuad"; a name stays as it is. */
function staffName(by: string): string {
  const local = by.includes('@') ? by.split('@')[0] : by;
  return by.includes('@') ? local.charAt(0).toUpperCase() + local.slice(1) : by;
}

function clockOf(m: number): string {
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Problems first (red, then amber), then by start time; ties by id so refreshes never shuffle. */
export function sortRooms(rooms: RoomView[]): RoomView[] {
  const rank: Record<Level, number> = { red: 0, amber: 1, none: 2 };
  return [...rooms].sort((a, b) => rank[a.level] - rank[b.level] || (a.startMin ?? 9999) - (b.startMin ?? 9999) || a.id.localeCompare(b.id));
}

// ── The day line ───────────────────────────────────────────────────────

export interface DayLine { tone: 'red' | 'amber' | 'blue' | 'grey'; text: string }
export function dayLine(rooms: RoomView[] | null): DayLine {
  if (!rooms) return { tone: 'grey', text: 'Can’t check the rooms right now' };
  const need = rooms.filter((r) => r.level !== 'none');
  if (!need.length) return { tone: 'blue', text: rooms.length ? 'All quiet' : 'No drills running right now' };
  const n = need.reduce((sum, r) => sum + r.problems.length, 0);
  return { tone: need.some((r) => r.level === 'red') ? 'red' : 'amber', text: `${plural(n, 'problem')} need${n === 1 ? 's' : ''} you` };
}

// ── The calendar: timeline, next 2 hours, tomorrow's check ─────────────

export type BarState = 'finished' | 'running' | 'upcoming';
export interface Bar { id: string; label: string; title: string; facilitator: string | null; startMin: number; endMin: number; state: BarState; level: Level; roomId: string | null }

/** Today's or tomorrow's sessions as bars; a problem room colours its bar. */
export function timelineBars(events: CalendarSession[], rooms: RoomView[], day: string, now = Date.now()): Bar[] {
  return events
    .filter((e) => e.starts_at && wibDate(e.starts_at) === day && e.status !== 'cancelled')
    .map((e) => {
      const s = Date.parse(e.starts_at!); const en = e.ends_at ? Date.parse(e.ends_at) : s + 45 * MIN;
      const startMin = wibMinutes(s); const endMin = Math.max(startMin + 5, wibMinutes(en));
      const room = rooms.find((r) => r.title === e.title && r.startMin !== null && Math.abs(r.startMin - startMin) <= 15) ?? null;
      const state: BarState = room ? 'running' : en <= now ? 'finished' : s <= now ? 'running' : 'upcoming';
      return { id: e.event_id, label: `${clockOf(startMin)}`, title: e.title, facilitator: e.facilitator_name, startMin, endMin, state, level: room?.level ?? 'none', roomId: room?.id ?? null };
    })
    .sort((a, b) => a.startMin - b.startMin);
}

/** Lanes so bars never overlap (each bar goes in the first lane free by its start). */
export function laneOf(bars: Bar[]): number[] {
  const ends: number[] = [];
  return bars.map((b) => {
    let li = ends.findIndex((e) => e <= b.startMin);
    if (li < 0) { ends.push(0); li = ends.length - 1; }
    ends[li] = b.endMin + 5;
    return li;
  });
}

export interface SessionCheck {
  id: string;
  time: string;
  title: string;
  where: string;
  facilitator: string | null;
  booked: number;
  /** "/ 4–6", "/ max 6", or "" */
  need: string;
  issue: { head: string; todo: string } | null;
}

const isLive = (e: CalendarSession) => e.event_type === 'workshop' || e.event_type === 'live_coach' || e.event_type === 'live_coach_slot';

/** What a coming session still needs: a facilitator, enough learners, a confirmed venue. */
export function sessionCheck(e: CalendarSession): SessionCheck {
  const min = e.min_participants ?? null; const max = e.max_participants || null;
  const need = min && max ? `/ ${min}–${max}` : max ? `/ max ${max}` : '';
  let issue: SessionCheck['issue'] = null;
  if (isLive(e) && !e.facilitator_name) issue = { head: 'No facilitator yet', todo: 'Assign one in Calendar' };
  else if (min && e.learner_count < min) issue = { head: `${e.learner_count} booked, needs ${min}`, todo: 'Invite one more, or move them' };
  else if (e.venue && e.venue.confirmed === false) issue = { head: 'Venue not confirmed', todo: 'Confirm with the venue' };
  return {
    id: e.event_id,
    time: e.starts_at ? wibClock(e.starts_at) : '—',
    title: e.title,
    // Only what the server says: a place when it sends one, "Online" when it says there's none, else nothing.
    where: e.venue?.name ? `In person · ${e.venue.name}` : e.venue === null ? 'Online' : '',
    facilitator: e.facilitator_name,
    booked: e.learner_count,
    need,
    issue,
  };
}

/**
 * Operations' sessions (19 Oct): facilitated sessions from the scheduler's calendar, real ones only
 * (a "possible" slot from someone's availability is not a session), plus AI coach drills from the
 * admin calendar, AI slots only once someone has booked. The admin calendar's facilitated half
 * fails on the server since 4 Sept (Scoring fixing), and it can't tell real from possible.
 */
export function operationsEvents(slots: SchedulerSlot[], adminEvents: CalendarSession[]): CalendarSession[] {
  const live: CalendarSession[] = slots
    .filter((s) => s.is_materialised && s.session_id && s.status !== 'cancelled' && s.status !== 'draft')
    .map((s) => ({
      event_id: s.session_id!,
      event_type: 'workshop',
      starts_at: s.scheduled_start,
      ends_at: s.scheduled_end ?? (s.duration_minutes ? new Date(Date.parse(s.scheduled_start) + s.duration_minutes * MIN).toISOString() : null),
      status: s.status,
      title: s.workshop_short_title || s.workshop_title || 'Skill drill',
      facilitator_name: s.facilitator_name,
      learner_count: s.current_bookings ?? 0,
      max_participants: s.max_participants ?? 0,
      min_participants: s.min_participants ?? null,
      link: null,
    }));
  const ai = adminEvents.filter((e) => e.event_type !== 'workshop' && (e.event_type === 'ai_coach' || e.learner_count > 0));
  return [...live, ...ai].sort((a, b) => (a.starts_at ?? '9').localeCompare(b.starts_at ?? '9'));
}

/** Sessions starting in the next 2 hours, soonest first. */
export function nextTwoHours(events: CalendarSession[], now = Date.now()): SessionCheck[] {
  return events
    .filter((e) => e.starts_at && Date.parse(e.starts_at) > now && Date.parse(e.starts_at) <= now + 120 * MIN && e.status !== 'cancelled')
    .sort((a, b) => Date.parse(a.starts_at!) - Date.parse(b.starts_at!))
    .map((e) => sessionCheck(e));
}

/** The next session to start today, for "Next start 15:00 · in 41 min". */
export function nextStart(events: CalendarSession[], now = Date.now()): { at: string; inMin: number } | null {
  const next = events.filter((e) => e.starts_at && Date.parse(e.starts_at) > now && e.status !== 'cancelled' && wibDate(e.starts_at) === wibDate(now))
    .sort((a, b) => Date.parse(a.starts_at!) - Date.parse(b.starts_at!))[0];
  return next ? { at: wibClock(next.starts_at!), inMin: Math.round((Date.parse(next.starts_at!) - now) / MIN) } : null;
}

/** A key for a red problem, to sound and notify only once per new problem. */
export function redKeys(rooms: RoomView[]): string[] {
  return rooms.flatMap((r) => r.problems.filter((p) => p.level === 'red').map((p) => `${r.id}:${p.key}`));
}
